import { useEffect, useRef } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { getSyncRuns, type SyncRun } from '../../lib/api';
import { useDataFreshnessStore, type DataDomain } from '../../lib/stores';
import { useActivityStore, type ActivityStatus } from '../../lib/activityStore';

/**
 * Headless bridge (renders null). The SINGLE `/api/syncs` poll for the app: it
 * reconciles every server `SyncRun` into the Activity Center store (upsert by
 * serverRunId), and ABSORBS — verbatim — SyncLogPanel's load-bearing post-sync
 * cache-invalidation (KIND_MAP / CHANGE_FIELDS / shouldInvalidate + the
 * useDataFreshnessStore.bump). That invalidation is what keeps lists + detail
 * refresh banners firing after autosync; it must move intact or autosync
 * silently stops refreshing data.
 */

// ─── BEGIN: absorbed verbatim from SyncLogPanel.tsx (load-bearing) ───────────

// When a sync of `kind` finishes with real changes: refetch these list query-key
// prefixes live, and (if `domain` is set) flag that domain's detail pages stale.
const KIND_MAP: Record<string, { lists: string[]; domain?: DataDomain }> = {
  discourse:          { lists: ['discourse-topics', 'discourse-facets', 'discourse-sync-status', 'discourse-insights'], domain: 'discourse' },
  'azure-devops':     { lists: ['azure-work-items', 'azure-facets', 'azure-sync-status'], domain: 'azure' },
  zoho:               { lists: ['zoho-tickets', 'zoho-facets', 'zoho-insights', 'zoho-topics', 'zoho-agent-stats'], domain: 'zoho' },
  commits:            { lists: ['commits'], domain: 'commits' },
  knowledge:          { lists: ['knowledge-status', 'embed-activity'] },
  documents:          { lists: ['documents'] },
  release:            { lists: ['release-status', 'release-sessions', 'tracked-releases', 'release-metrics'] },
  entitlements:       { lists: ['entitlements', 'entitlement-suggestions', 'release-trains'] },
  'zoho-classify':    { lists: ['zoho-tickets', 'zoho-facets', 'zoho-insights'], domain: 'zoho' },
  'zoho-topics':      { lists: ['zoho-topics', 'zoho-insights', 'zoho-tickets', 'zoho-facets'] },
  'zoho-sentiment':   { lists: ['zoho-tickets', 'zoho-insights', 'zoho-facets'], domain: 'zoho' },
  'zoho-embed':       { lists: ['zoho-tickets', 'zoho-insights'] },
  'discourse-import': { lists: ['discourse-topics', 'discourse-facets', 'discourse-sync-status'] },
  'discourse-embed':  { lists: ['discourse-topics', 'discourse-insights'] },
};

// Stat fields whose >0 value means a completed run actually changed data. Used to
// avoid refetching open lists on the frequent no-op autosync cycles.
const CHANGE_FIELDS: Record<string, string[]> = {
  discourse: ['changed', 'failed'], 'azure-devops': ['upserted', 'failed'],
  commits: ['totalSynced', 'ciUpdated'], zoho: ['upserted', 'changed', 'threadsFetched'],
  'zoho-classify': ['classified'], 'zoho-topics': ['topics'], 'zoho-sentiment': ['analyzed'], 'zoho-embed': ['embedded'],
};

const TERMINAL = new Set<SyncRun['status']>(['completed', 'partial', 'stopped', 'interrupted', 'failed']);

/** Whether a finished run changed data worth refetching for. Completed runs are
 *  gated on their stats; other terminals (partial/failed/stopped/interrupted) and
 *  unknown stat shapes default to refetch. 'rejected' never reaches here. */
function shouldInvalidate(run: SyncRun): boolean {
  if (run.status !== 'completed') return true;
  const fields = CHANGE_FIELDS[run.kind];
  if (!fields || !run.stats) return true;
  return fields.some((f) => (run.stats?.[f] ?? 0) > 0);
}

// ─── END: absorbed verbatim from SyncLogPanel.tsx ────────────────────────────

/** Map a server SyncRun status onto the Activity Center's terminal vocabulary. */
function toActivityStatus(s: SyncRun['status']): ActivityStatus {
  switch (s) {
    case 'completed': return 'completed';
    case 'partial':   return 'partial';
    case 'failed':    return 'failed';
    case 'stopped':
    case 'interrupted':
    case 'rejected':  return 'stopped';
    case 'running':
    default:          return 'running';
  }
}

export function SyncActivityBridge() {
  const queryClient = useQueryClient();

  // Sync changes arrive via push ('sync' events → RealtimeBridge invalidates
  // ['sync-runs']). Keep only a relaxed 5s while-running poll for live progress;
  // no idle poll — the run-started event wakes this.
  const { data: runs } = useQuery({
    queryKey: ['sync-runs', 'all', 'def'],
    queryFn: () => getSyncRuns(),
    refetchInterval: (query) => {
      const data = query.state.data as SyncRun[] | undefined;
      return data?.some((r) => r.status === 'running') ? 5000 : false;
    },
  });

  // ── Load-bearing post-sync cache refresh (absorbed verbatim) ──────────────
  const bump = useDataFreshnessStore((s) => s.bump);
  const azureReorderActive = useDataFreshnessStore((s) => s.azureReorderActive);
  const prevStatusRef = useRef<Map<string, SyncRun['status']> | null>(null);
  const pendingListsRef = useRef<Set<string>>(new Set());

  useEffect(() => {
    if (!runs) return;

    // Flush list refetches deferred while the user was dragging Backlog rows.
    if (!azureReorderActive && pendingListsRef.current.size > 0) {
      for (const key of pendingListsRef.current) queryClient.invalidateQueries({ queryKey: [key] });
      pendingListsRef.current.clear();
    }

    const prev = prevStatusRef.current;
    // First observation: seed silently so historical terminal runs (on page load)
    // don't trigger an invalidation storm.
    if (prev === null) {
      prevStatusRef.current = new Map(runs.map((r) => [r.id, r.status]));
      return;
    }

    const lists = new Set<string>();
    const domains = new Set<DataDomain>();
    let anyTerminal = false;

    for (const r of runs) {
      const before = prev.get(r.id);
      // running→terminal, or a fast sync that finished between polls (unseen→terminal).
      const justFinished = TERMINAL.has(r.status) && (before === 'running' || before === undefined);
      if (!justFinished) continue;
      anyTerminal = true;
      if (!shouldInvalidate(r)) continue;
      const m = KIND_MAP[r.kind];
      if (m) {
        m.lists.forEach((k) => lists.add(k));
        if (m.domain) domains.add(m.domain);
      }
    }

    if (anyTerminal) queryClient.invalidateQueries({ queryKey: ['dashboard'] });
    for (const key of lists) {
      // Defer the Backlog list refetch while a drag-reorder is in progress.
      if (key === 'azure-work-items' && azureReorderActive) { pendingListsRef.current.add(key); continue; }
      queryClient.invalidateQueries({ queryKey: [key] });
    }
    for (const d of domains) bump(d);

    prevStatusRef.current = new Map(runs.map((r) => [r.id, r.status]));
  }, [runs, azureReorderActive, queryClient, bump]);

  // ── Reconcile each SyncRun into the Activity Center store ──────────────────
  // TRANSITION-BASED: a terminal run is shown in the Center exactly ONCE — at
  // the moment it transitions running→terminal during this session — and is
  // never re-introduced afterwards. Without this, every 2s poll re-upserts the
  // full /api/syncs history, and the store's auto-dismiss + re-upsert race
  // produces a rapid add/remove flicker (and on mobile a content-sized sheet
  // that resizes several times a second).
  //
  // `reconciledRef` persists ACROSS polls (separate from the load-bearing
  // `prevStatusRef` used by the invalidation effect above — that one is left
  // verbatim): for each run id it records the status we last pushed to the
  // store, so we can tell a fresh running→terminal transition (push once) from
  // a historical terminal or an already-transitioned one (never push).
  // The row's Stop action calls stopSyncRun(serverRunId) directly (see
  // ActivityRow), so we only need to carry serverRunId + stoppable here.
  const reconciledRef = useRef<Map<string, ActivityStatus>>(new Map());

  useEffect(() => {
    if (!runs) return;
    const upsert = useActivityStore.getState().upsert;

    // A failure this session never watched start (b-01896). The transition gate
    // below fires only for a run we personally saw running — but a run that dies
    // on its FIRST request (an expired credential, say) goes from unseen straight
    // to terminal inside a single poll, and the poll stops entirely while nothing
    // is running. So being dropped WAS the normal path for a hard failure, which
    // is how nine dead Azure DevOps syncs left the Activity Center empty for an
    // hour while the backlog quietly stopped updating.
    //
    // Surface such a failure, but only while it is still the last word on that
    // sync: the newest run of its kind, and it failed. A later run that got
    // through retires it silently. That makes this one sticky row per BROKEN
    // sync, never a replay of history. 'partial' is deliberately excluded — it
    // auto-dismisses, so replaying it on page load would only flicker.
    const newestByKind = new Map<string, SyncRun>();
    for (const r of runs) {
      if (r.status === 'rejected') continue;
      const cur = newestByKind.get(r.kind);
      if (!cur || (r.startedAt ?? '') > (cur.startedAt ?? '')) newestByKind.set(r.kind, r);
    }
    const isStandingFailure = (r: SyncRun) => r.status === 'failed' && newestByKind.get(r.kind)?.id === r.id;

    for (const r of runs) {
      // 'rejected' rows never started — don't surface them as activities.
      if (r.status === 'rejected') continue;
      const status = toActivityStatus(r.status);
      const prev = reconciledRef.current.get(r.id);

      if (status === 'running') {
        // Running: upsert every poll so progress stays live.
        upsert({
          id: `sync:${r.id}`,
          source: 'sync',
          kind: r.kind,
          title: r.label,
          status,
          message: r.message,
          progress: r.progress,
          error: r.error ?? undefined,
          stoppable: true,
          serverRunId: r.id,
          startedAt: r.startedAt,
          finishedAt: r.finishedAt ?? undefined,
        });
        reconciledRef.current.set(r.id, 'running');
        continue;
      }

      // Terminal (completed/stopped/failed). Surface it as a genuine
      // running→terminal transition — i.e. WE introduced it as running and it
      // just finished — or, on first sight, as a standing failure (above). An
      // ALREADY-terminal prev never re-upserts (already transitioned, maybe
      // already dismissed) — that re-upsert is the flicker. Either way, record
      // the status so we never re-introduce this run later.
      if (prev === 'running' || (prev === undefined && isStandingFailure(r))) {
        // Single terminal upsert → schedules the 10s auto-dismiss (or stays
        // sticky if failed). Recording the status below is what holds this to
        // ONE upsert: the next poll reads prev === 'failed', not undefined.
        upsert({
          id: `sync:${r.id}`,
          source: 'sync',
          kind: r.kind,
          title: r.label,
          status,
          message: r.message,
          progress: r.progress,
          error: r.error ?? undefined,
          // A terminal row can't be stopped.
          stoppable: false,
          serverRunId: r.id,
          startedAt: r.startedAt,
          finishedAt: r.finishedAt ?? undefined,
        });
      }
      reconciledRef.current.set(r.id, status);
    }
  }, [runs]);

  return null;
}
